<template>
  <div class="modal-overlay" @click.self="$emit('cancel')">
    <div class="modal-container">
      <div class="tw-flex items-center justify-between mb-4">
        <h2 class="text-xl font-semibold">{{ isEditing ? 'Edit announcement' : 'Create announcement' }}</h2>
        <button class="text-gray-400 hover:text-gray-600" @click="$emit('cancel')">
          <icon name="cross" size="16"/>
        </button>
      </div>

      <form-group label="Message" required>
        <ds-textarea
          v-model="form.message"
          placeholder="Describe what has changed and why users need to review..."
          autoresize
          maxlength="500"
        />
        <p class="text-sm text-gray-400 mt-1">{{ form.message.length }} / 500</p>
      </form-group>

      <form-group label="Link policy documents" required class="mt-3">
        <p class="text-sm text-gray-500 mb-2">Select pending policy versions to include in this announcement.</p>
        <div v-if="pendingPolicies.length === 0" class="text-sm text-gray-400">
          No pending policy versions available. Create a policy version first.
        </div>
        <div v-for="policy in pendingPolicies" :key="policy.id" class="tw-flex items-center gap-2 mb-1">
          <input
            type="checkbox"
            :id="'policy-' + policy.id"
            :value="policy.id"
            v-model="selectedPolicyIds"
          />
          <label :for="'policy-' + policy.id" class="text-sm">
            {{ typeLabel(policy.type) }} — {{ policy.version_name }}
          </label>
        </div>
      </form-group>

      <form-group label="Effective date" required class="mt-3">
        <ds-input v-model="form.scheduled_date" type="date" :min="todayDate"/>
      </form-group>

      <form-group label="Require acceptance?" required class="mt-3">
        <div class="tw-flex items-center gap-4">
          <radio-button
            name="requires_acceptance"
            label="Yes"
            :val="true"
            :model-value="form.requires_acceptance"
            @update:model-value="form.requires_acceptance = $event"
            inline
          />
          <radio-button
            name="requires_acceptance"
            label="No"
            :val="false"
            :model-value="form.requires_acceptance"
            @update:model-value="form.requires_acceptance = $event"
            inline
          />
        </div>
      </form-group>

      <div v-if="form.requires_acceptance" class="info-banner mt-3">
        The popup blocks access until the user accepts.
      </div>

      <div v-if="errors.length > 0" class="mt-3">
        <p v-for="error in errors" :key="error" class="text-sm" style="color: red;">{{ error }}</p>
      </div>

      <div class="tw-flex gap-2 mt-4">
        <ds-button
          :label="isEditing ? 'Update announcement' : 'Schedule announcement'"
          variant="rounded"
          @click="submit"
          :disabled="submitting"
        />
        <ds-button label="Cancel" variant="rounded-minimal" @click="$emit('cancel')"/>
      </div>
    </div>
  </div>
</template>

<script>
  import { defineComponent } from 'vue'
  import DsButton from '@/components/DsButton/DsButton.vue'
  import DsInput from '@/components/Form/DsInput.vue'
  import DsTextarea from '@/components/Form/DsTextarea.vue'
  import FormGroup from '@/components/Form/FormGroup.vue'
  import Icon from '@/components/Icon/Icon.vue'
  import RadioButton from '@/components/Form/RadioButton.vue'
  import * as AnnouncementsApi from '@/Domain/Policy/Api/announcements'

  const TYPE_LABELS = {
    'terms-of-use': 'Terms of Use',
    'privacy-policy': 'Privacy Policy',
    'cookie-policy': 'Cookie Policy',
  }

  export default defineComponent({
    name: 'AnnouncementForm',
    components: { DsButton, DsInput, DsTextarea, FormGroup, Icon, RadioButton },
    props: {
      announcement: {
        type: Object,
        default: null,
      },
      pendingPolicies: {
        type: Array,
        default: () => [],
      },
    },
    emits: ['save', 'cancel'],
    data() {
      return {
        form: {
          message: this.announcement?.message || '',
          scheduled_date: this.announcement?.scheduled_date || this.announcement?.scheduledDate || '',
          requires_acceptance: this.announcement?.requires_acceptance ?? this.announcement?.requiresAcceptance ?? false,
        },
        selectedPolicyIds: this.getInitialSelectedPolicies(),
        errors: [],
        submitting: false,
      }
    },
    computed: {
      isEditing() {
        return this.announcement !== null
      },
      todayDate() {
        return new Date().toISOString().split('T')[0]
      },
    },
    methods: {
      typeLabel(type) {
        return TYPE_LABELS[type] || type
      },
      getInitialSelectedPolicies() {
        if (!this.announcement?.linked_policies && !this.announcement?.linkedPolicies) {
          return []
        }
        const policies = this.announcement.linked_policies || this.announcement.linkedPolicies || []
        return policies.map(p => p.id)
      },
      buildPayload() {
        const payload = {
          message: this.form.message,
          scheduled_date: this.form.scheduled_date,
          requires_acceptance: this.form.requires_acceptance,
          terms_of_use_policy_id: null,
          privacy_policy_id: null,
          cookie_policy_id: null,
        }

        for (const policy of this.pendingPolicies) {
          if (!this.selectedPolicyIds.includes(policy.id)) continue

          if (policy.type === 'terms-of-use') {
            payload.terms_of_use_policy_id = policy.id
          } else if (policy.type === 'privacy-policy') {
            payload.privacy_policy_id = policy.id
          } else if (policy.type === 'cookie-policy') {
            payload.cookie_policy_id = policy.id
          }
        }

        return payload
      },
      async submit() {
        this.errors = []
        this.submitting = true

        try {
          const payload = this.buildPayload()

          if (this.isEditing) {
            await AnnouncementsApi.update(this.announcement.id, payload)
          } else {
            await AnnouncementsApi.create(payload)
          }

          this.$emit('save')
        } catch (e) {
          if (e && e.errors) {
            this.errors = Object.values(e.errors).flat()
          } else if (e && e.message) {
            this.errors = [e.message]
          } else {
            this.errors = ['An unexpected error occurred.']
          }
        }

        this.submitting = false
      },
    },
  })
</script>

<style scoped>
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .modal-container {
    background: white;
    border-radius: 8px;
    padding: 24px;
    width: 560px;
    max-width: 90vw;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  }

  .info-banner {
    background: #e3f2fd;
    border: 1px solid #90caf9;
    border-radius: 6px;
    padding: 10px 14px;
    font-size: 13px;
    color: #1565c0;
  }

  :deep(.radio--inline) {
    align-items: center;
  }
</style>
